// Lightweight Web-Audio synthesised sounds — no external assets.
// All synth tones use an AudioContext that lazy-initialises on first call so
// autoplay policies are respected.

let ctx = null;
let muted = false;

try {
  const stored = typeof window !== "undefined" && window.localStorage?.getItem("zenjong_mute");
  muted = stored === "1";
} catch {
  // ignore
}

function getCtx() {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

function blip({ freq = 440, dur = 0.08, type = "sine", gain = 0.12, slideTo = null, attack = 0.004, release = 0.06 }) {
  if (muted) return;
  const ac = getCtx();
  if (!ac) return;
  const t0 = ac.currentTime;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur + release);
  osc.connect(g).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + dur + release + 0.02);
}

// Short decaying filtered-noise "crack" — the gritty body of an impact SFX.
function noiseCrack({ dur = 0.16, gain = 0.24, lp = 3000, type = "lowpass", q = 0.7, endFreq = 400 }) {
  if (muted) return;
  const ac = getCtx();
  if (!ac) return;
  const t0 = ac.currentTime;
  const frames = Math.max(1, Math.floor(ac.sampleRate * dur));
  const buffer = ac.createBuffer(1, frames, ac.sampleRate);
  const ch = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) {
    ch[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / frames, 2.2);
  }
  const src = ac.createBufferSource();
  src.buffer = buffer;
  const filter = ac.createBiquadFilter();
  filter.type = type;
  filter.Q.setValueAtTime(q, t0);
  filter.frequency.setValueAtTime(lp, t0);
  filter.frequency.exponentialRampToValueAtTime(endFreq, t0 + dur);
  const g = ac.createGain();
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filter).connect(g).connect(ac.destination);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}

export const sfx = {
  tap: () => blip({ freq: 520, dur: 0.05, type: "triangle", gain: 0.08 }),
  // Heavy stone-on-stone CRUSH — deep rumble + gritty grind + a sharp crack
  // and a rubble tail. Reads as two rocks being crushed together.
  smash: () => {
    blip({ freq: 90, dur: 0.2, type: "sine", gain: 0.34, slideTo: 38 });
    noiseCrack({ dur: 0.18, gain: 0.32, lp: 1500, type: "bandpass", q: 0.8, endFreq: 500 });
    noiseCrack({ dur: 0.09, gain: 0.28, lp: 3000, endFreq: 400 });
    blip({ freq: 200, dur: 0.1, type: "sawtooth", gain: 0.1, slideTo: 90 });
    setTimeout(() => noiseCrack({ dur: 0.12, gain: 0.2, lp: 1000, type: "bandpass", q: 1.0, endFreq: 300 }), 45);
  },
  select: () => blip({ freq: 660, dur: 0.06, type: "sine", gain: 0.1, slideTo: 880 }),
  match: () => {
    blip({ freq: 660, dur: 0.08, type: "sine", gain: 0.12, slideTo: 990 });
    setTimeout(() => blip({ freq: 1320, dur: 0.1, type: "triangle", gain: 0.1 }), 90);
  },
  invalid: () => blip({ freq: 220, dur: 0.14, type: "square", gain: 0.07, slideTo: 150 }),
  shuffle: () => {
    [0, 60, 120].forEach((d, i) => setTimeout(() =>
      blip({ freq: 440 + i * 90, dur: 0.07, type: "sine", gain: 0.08 }), d));
  },
  hint: () => blip({ freq: 880, dur: 0.12, type: "sine", gain: 0.09, slideTo: 1320 }),
  undo: () => blip({ freq: 500, dur: 0.1, type: "triangle", gain: 0.09, slideTo: 380 }),
  // Coin cash-register chime — used when claiming challenge/streak rewards.
  // Two bright bell-tones in close succession to feel like coins dropping
  // into a tray, similar to mobile banking apps.
  coinCash: () => {
    blip({ freq: 1320, dur: 0.06, type: "triangle", gain: 0.14, slideTo: 1760 });
    setTimeout(() => blip({ freq: 1760, dur: 0.08, type: "triangle", gain: 0.13, slideTo: 2349 }), 70);
    setTimeout(() => blip({ freq: 2093, dur: 0.12, type: "sine", gain: 0.12 }), 160);
  },
  win: () => {
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) =>
      setTimeout(() => blip({ freq: f, dur: 0.18, type: "triangle", gain: 0.14 }), i * 110));
  },
  // Final "pop" + ascending sparkle when the last pair clears.
  levelPop: () => {
    // Tight bass pop
    blip({ freq: 140, dur: 0.08, type: "sine", gain: 0.22, slideTo: 70 });
    // Sparkle arpeggio
    [784, 988, 1319, 1760, 2093].forEach((f, i) =>
      setTimeout(() => blip({ freq: f, dur: 0.1, type: "triangle", gain: 0.12 }), 70 + i * 60));
  },
  fail: () => {
    [400, 320, 220].forEach((f, i) =>
      setTimeout(() => blip({ freq: f, dur: 0.16, type: "sine", gain: 0.1 }), i * 130));
  },
};

export function setMuted(next) {
  muted = !!next;
  try {
    if (typeof window !== "undefined") {
      window.localStorage?.setItem("zenjong_mute", muted ? "1" : "0");
    }
  } catch {
    // ignore
  }
}

export function isMuted() {
  // Respect both the legacy in-page mute AND the new Settings panel toggles.
  // The Settings sheet writes "fitjong_sfx_off=1" — when set we mute synth SFX.
  if (typeof window !== "undefined") {
    try {
      if (window.localStorage?.getItem("fitjong_sfx_off") === "1") return true;
    } catch (_) {}
  }
  return muted;
}

// Coach voice has its own toggle so users who like the SFX but find the
// spoken cues distracting can mute only the voice.
export function isCoachVoiceMuted() {
  if (typeof window !== "undefined") {
    try {
      if (window.localStorage?.getItem("fitjong_voice_off") === "1") return true;
    } catch (_) {}
  }
  return muted;
}
